// components/CheckoutPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config/config';

// Components (same as ProductView)
import FlashBar from "../components/FlashBar";
import ShareableNav from "../components/ShareableNav";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";      // For cart modal in Nav
import { CreditCard, Smartphone, Lock, Check,MessageSquare,HelpCircle,ArrowLeft,ShoppingBag, User, MapPin, Phone, Clock, Navigation, Mail, ExternalLink, Search, Filter, ChevronRight } from 'lucide-react';

const CACHE_KEY = 'checkout_page_cache';
const CACHE_TTL = 24*60*60 * 1000; // 1 minute

const CheckoutPage = () => {
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const [step, setStep] = useState(1);
  const [stores, setStores] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('mpesa');

  // Layout configs – start null, filled from cache instantly
  const [flashConfig, setFlashConfig] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [checkoutConfig, setCheckoutConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);
  const [cartItems, setCartItems] = useState([]); 

  const defaultConfig = {
    activeDesign: 'side-by-side', // 'side-by-side', 'wizard', 'compact'
    title: 'Checkout',
    shippingTitle: 'Shipping Information',
    paymentTitle: 'Payment Method',
    reviewTitle: 'Review Your Order',
    emptyCartMessage: 'Your cart is empty',
    continueShoppingText: 'Continue Shopping',
    checkoutText: 'Complete Order',
    customStyles: {
      background: 'bg-gray-50',
      textColor: 'text-gray-900',
      buttonBg: 'bg-gray-900',
      buttonText: 'text-white',
      accentColor: 'text-green-600',
    },
  };

  const finalConfig = { ...defaultConfig, ...checkoutConfig };
  const {
    activeDesign,
    title,
    shippingTitle,
    paymentTitle,
    reviewTitle,
    emptyCartMessage,
    continueShoppingText,
    checkoutText,
    customStyles,
  } = finalConfig;

  // ────────────────────────────────────────────────
  // Cache Helpers
  // ────────────────────────────────────────────────
  const getCached = () => {
    const item = localStorage.getItem(CACHE_KEY);
    if (!item) return null;
    try {
      const { data, timestamp } = JSON.parse(item);
      if (Date.now() - timestamp < CACHE_TTL) return data;
      localStorage.removeItem(CACHE_KEY);
      return null;
    } catch {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const setCached = (data) => {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  };

  // ────────────────────────────────────────────────
  // Load Layout → Cache FIRST, then background API refresh
  // ────────────────────────────────────────────────
 

const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email Us',
      value: 'support@yourstore.com',
      description: 'Send us an email anytime',
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      icon: Phone,
      label: 'Call Us',
      value: '+254 712 345 678',
      description: 'Mon-Fri from 8am to 6pm',
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      icon: MapPin,
      label: 'Visit Us',
      value: '123 Main Street, Nairobi',
      description: 'Kenya, East Africa',
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    },
    {
      icon: Clock,
      label: 'Working Hours',
      value: 'Mon - Fri: 8:00 AM - 6:00 PM',
      description: 'Sat: 9:00 AM - 4:00 PM',
      color: 'text-orange-600',
      bg: 'bg-orange-100'
    }
  ];

  const inquiryTypes = [
    {
      value: 'general',
      icon: MessageSquare,
      label: 'General Inquiry',
      description: 'Questions about our services'
    },
    {
      value: 'support',
      icon: HelpCircle,
      label: 'Customer Support',
      description: 'Need help with an order'
    },
    {
      value: 'sales',
      icon: ShoppingBag,
      label: 'Sales',
      description: 'Interested in bulk orders'
    }
  ];

  const faqs = [
    {
      question: 'What are your shipping options?',
      answer: 'We offer standard (3-5 days) and express (1-2 days) shipping across Kenya.'
    },
    {
      question: 'How can I track my order?',
      answer: 'You will receive a tracking number via email once your order ships.'
    },
    {
      question: 'What is your return policy?',
      answer: 'We offer 30-day hassle-free returns on all products.'
    }
  ];


 

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  // Safe config handling
  const actualCheckoutConfig = 
    checkoutConfig?.checkoutSettings && typeof checkoutConfig.checkoutSettings === 'object' && !Array.isArray(checkoutConfig.checkoutSettings)
      ? checkoutConfig.checkoutSettings
      : (typeof checkoutConfig === 'object' && !Array.isArray(checkoutConfig) ? checkoutConfig : {});

  const checkoutDefault = {
    activeDesign: 'side-by-side',
    devMode: false,
    customStyles: {},
  };

  const finalCheckoutConfig = { ...checkoutDefault, ...actualCheckoutConfig };
  const { activeDesign: checkoutDesign, devMode: checkoutDevMode, customStyles: checkoutStyles } = finalCheckoutConfig;

  const containerStyles = {
    background: checkoutDevMode && checkoutStyles.background ? checkoutStyles.background : undefined,
    color: checkoutDevMode && checkoutStyles.textColor ? checkoutStyles.textColor : undefined,
  };

 

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  const shipping = subtotal > 100 ? 0 : 10;
  const tax = subtotal * 0.16;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };


  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedStore, setSelectedStore] = useState(null);


   const fetchAllStores = async () => {
    try {
      const [categoriesRes] = await Promise.all([
        axios.get(`${config.API_URL}/api/pullStores`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      const storesArray =categoriesRes.data?.stores ||
        [];
      setStores(storesArray);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  useEffect(() => {
    fetchAllStores();
  }, [token]);

  // Store locations data
  const storez = [
    {
      id: 1,
      name: 'Westlands Flagship Store',
      city: 'Nairobi',
      address: '123 Westlands Avenue, Westlands',
      zipCode: '00100',
      phone: '+254 712 345 678',
      email: 'westlands@store.com',
      hours: {
        weekday: 'Mon - Fri: 9:00 AM - 8:00 PM',
        saturday: 'Sat: 9:00 AM - 6:00 PM',
        sunday: 'Sun: 10:00 AM - 5:00 PM'
      },
      features: ['Click & Collect', 'Personal Shopping', 'Gift Wrapping', 'Alterations'],
      image: 'bg-gradient-to-br from-blue-100 to-blue-200',
      coordinates: { lat: -1.2641, lng: 36.8047 },
      isOpen: true,
      isFlagship: true
    },
    {
      id: 2,
      name: 'Sarit Centre Store',
      city: 'Nairobi',
      address: 'Sarit Centre Mall, Westlands',
      zipCode: '00100',
      phone: '+254 712 345 679',
      email: 'sarit@store.com',
      hours: {
        weekday: 'Mon - Fri: 10:00 AM - 8:00 PM',
        saturday: 'Sat: 10:00 AM - 8:00 PM',
        sunday: 'Sun: 10:00 AM - 6:00 PM'
      },
      features: ['Click & Collect', 'Gift Wrapping', 'Returns'],
      image: 'bg-gradient-to-br from-purple-100 to-purple-200',
      coordinates: { lat: -1.2621, lng: 36.7948 },
      isOpen: true,
      isFlagship: false
    },
    {
      id: 3,
      name: 'The Hub Karen',
      city: 'Nairobi',
      address: 'The Hub Mall, Karen Road',
      zipCode: '00200',
      phone: '+254 712 345 680',
      email: 'karen@store.com',
      hours: {
        weekday: 'Mon - Fri: 10:00 AM - 7:00 PM',
        saturday: 'Sat: 10:00 AM - 7:00 PM',
        sunday: 'Sun: 11:00 AM - 6:00 PM'
      },
      features: ['Click & Collect', 'Personal Shopping', 'Returns'],
      image: 'bg-gradient-to-br from-green-100 to-emerald-200',
      coordinates: { lat: -1.3194, lng: 36.7085 },
      isOpen: true,
      isFlagship: false
    },
    {
      id: 4,
      name: 'Mombasa Road Store',
      city: 'Mombasa',
      address: '456 Moi Avenue, Mombasa CBD',
      zipCode: '80100',
      phone: '+254 712 345 681',
      email: 'mombasa@store.com',
      hours: {
        weekday: 'Mon - Fri: 9:00 AM - 7:00 PM',
        saturday: 'Sat: 9:00 AM - 7:00 PM',
        sunday: 'Sun: 10:00 AM - 5:00 PM'
      },
      features: ['Click & Collect', 'Gift Wrapping'],
      image: 'bg-gradient-to-br from-amber-100 to-orange-200',
      coordinates: { lat: -4.0435, lng: 39.6682 },
      isOpen: true,
      isFlagship: false
    },
    {
      id: 5,
      name: 'Kisumu Mega Plaza',
      city: 'Kisumu',
      address: 'Mega Plaza, Oginga Odinga Road',
      zipCode: '40100',
      phone: '+254 712 345 682',
      email: 'kisumu@store.com',
      hours: {
        weekday: 'Mon - Fri: 9:00 AM - 6:00 PM',
        saturday: 'Sat: 9:00 AM - 6:00 PM',
        sunday: 'Sun: 10:00 AM - 4:00 PM'
      },
      features: ['Click & Collect', 'Returns'],
      image: 'bg-gradient-to-br from-cyan-100 to-blue-200',
      coordinates: { lat: -0.0917, lng: 34.7680 },
      isOpen: false,
      isFlagship: false
    },
    {
      id: 6,
      name: 'Nakuru Town Store',
      city: 'Nakuru',
      address: 'Kenyatta Avenue, Nakuru Town',
      zipCode: '20100',
      phone: '+254 712 345 683',
      email: 'nakuru@store.com',
      hours: {
        weekday: 'Mon - Fri: 9:00 AM - 6:00 PM',
        saturday: 'Sat: 9:00 AM - 6:00 PM',
        sunday: 'Sun: Closed'
      },
      features: ['Click & Collect', 'Gift Wrapping'],
      image: 'bg-gradient-to-br from-pink-100 to-rose-200',
      coordinates: { lat: -0.3031, lng: 36.0800 },
      isOpen: true,
      isFlagship: false
    }
  ];

  const cities = ['All Cities', ...new Set(stores?.map(store => store.city))];

   // Filter stores
  const filteredStores = stores.filter(store => {
    const matchesCity = selectedCity === 'all' || store.city === selectedCity;
    const matchesSearch = searchQuery === '' || 
      store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      store.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      store.address.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCity && matchesSearch;
  });



  // Design 1: Side-by-Side Layout
  const Design1 = () => (
       <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Find a Store Near You</h1>
            <p className="text-xl text-gray-300">Visit us at any of our {stores.length} locations across Kenya</p>
          </div>

          {/* Search & Filter */}
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by city, store name, or address..."
                  className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 focus:ring-4 focus:ring-white/20 focus:outline-none"
                />
              </div>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="px-6 py-4 rounded-xl text-gray-900 font-medium focus:ring-4 focus:ring-white/20 focus:outline-none cursor-pointer"
              >
                {cities.map((city) => (
                  <option key={city} value={city === 'All Cities' ? 'all' : city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            {searchQuery && (
              <p className="mt-3 text-gray-300 text-center">
                Found {filteredStores.length} store{filteredStores.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Map Placeholder 
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 mb-8">
        <div className="aspect-video md:aspect-[21/9] bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl shadow-xl flex items-center justify-center relative overflow-hidden">
          <div className="text-center z-10">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 font-semibold text-lg">Interactive Map</p>
            <p className="text-sm text-gray-500">Store locations across Kenya</p>
          </div>
         
          {filteredStores?.map((store, idx) => (
            <div
              key={store.id}
              className="absolute w-8 h-8 bg-red-500 rounded-full border-4 border-white shadow-lg animate-pulse"
              style={{
                left: `${20 + (idx * 12)}%`,
                top: `${30 + (idx * 8)}%`
              }}
            />
          ))}
        </div>
      </div>
      */}

      {/* Stores Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        {filteredStores.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStores?.map((store) => (
              <div
                key={store.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
              >
                {/* Store Image */}
                <div className={`h-48 ${store.image} relative`}>
                 {/* {store.isFlagship && (
                    <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-3 py-1.5 rounded-full text-xs font-bold">
                      FLAGSHIP STORE
                    </div>
                  )} */}
                  <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-semibold ${
                    store.isOpen 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-500 text-white'
                  }`}>
                    {store.isOpen ? 'Open Now' : 'Closed'}
                  </div>
                </div>

                {/* Store Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{store.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{store.city}</p>

                  {/* Address */}
                  <div className="flex items-start gap-3 mb-3">
                    <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-700">
                      <p>{store.address}</p>
                      <p>{store.zipCode}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3 mb-3">
                    <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <a href={`tel:${store.phone}`} className="text-sm text-gray-700 hover:text-gray-900">
                      {store.phone}
                    </a>
                  </div>

                  {/* Hours 
                  <div className="flex items-start gap-3 mb-4">
                    <Clock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-700">
                      <p>{store.hours.weekday}</p>
                      <p>{store.hours.saturday}</p>
                      <p>{store.hours.sunday}</p>
                    </div>
                  </div>
                  */}

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {store.features?.map((feature, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setSelectedStore(store)}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Details
                    </button>
                   {/* <button className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
                      <Navigation className="w-4 h-4" />
                      Directions
                    </button>*/}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Store Details Modal */}
      {selectedStore && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className={`h-48 ${selectedStore.image} relative`}>
              {/*{selectedStore.isFlagship && (
                <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-bold">
                  FLAGSHIP STORE
                </div>
              )}*/}
              <button
                onClick={() => setSelectedStore(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-lg"
              >
                <span className="text-2xl text-gray-600">×</span>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedStore.name}</h2>
                  <p className="text-gray-600">{selectedStore.city}</p>
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  selectedStore.isOpen 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {selectedStore.isOpen ? 'Open Now' : 'Closed'}
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                    <p className="text-gray-700">{selectedStore.address}</p>
                    <p className="text-gray-700">{selectedStore.zipCode}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a href={`tel:${selectedStore.phone}`} className="text-gray-700 hover:text-gray-900">
                      {selectedStore.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href={`mailto:${selectedStore.email}`} className="text-gray-700 hover:text-gray-900">
                      {selectedStore.email}
                    </a>
                  </div>
                </div>

                {/*<div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Opening Hours</h3>
                    <div className="space-y-1 text-gray-700">
                      <p>{selectedStore.hours.weekday}</p>
                      <p>{selectedStore.hours.saturday}</p>
                      <p>{selectedStore.hours.sunday}</p>
                    </div>
                  </div>
                </div>*/}
              </div>

              {/* Store Features */}
              {/*  <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Available Services</h3>
                <div className="grid grid-cols-2 gap-3">
                  {selectedStore.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <ChevronRight className="w-3 h-3 text-green-600" />
                      </div>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

          
              <div className="flex gap-3">
                <button className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
                  <Navigation className="w-5 h-5" />
                  Get Directions
                </button>
                <button className="flex-1 border-2 border-gray-900 text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call Store
                </button>
              </div>*/}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Design 2: Multi-Step Wizard (Guided Flow)
  const Design2 = () => (
    <div className={`min-h-screen ${customStyles.background}`}>
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="flex justify-between mb-12 max-w-md mx-auto">
          {['Shipping', 'Payment', 'Review'].map((label, index) => (
            <div key={label} className="flex-1 text-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto font-bold text-lg transition-all ${
                  step > index + 1
                    ? 'bg-green-600 text-white'
                    : step === index + 1
                    ? 'bg-gray-900 text-white ring-4 ring-gray-200'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {step > index + 1 ? <Check size={24} /> : index + 1}
              </div>
              <p className={`mt-3 font-medium ${step === index + 1 ? 'text-gray-900' : 'text-gray-500'}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {step === 1 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{shippingTitle}</h2>
              {/* Shipping form */}
              <button
                onClick={() => setStep(2)}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                Continue to Payment
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{paymentTitle}</h2>
              {/* Payment selection */}
              <button
                onClick={() => setStep(3)}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                Review & Confirm
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{reviewTitle}</h2>
              {/* Summary */}
              <button
                onClick={handleSubmit}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                {checkoutText} - ${total.toFixed(2)}
              </button>
            </>
          )}
        </div>

        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className="mt-6 text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <ArrowLeft size={20} /> Back
          </button>
        )}
      </div>
    </div>
  );

  // Design 3: Compact Mobile-First
  const Design3 = () => (
    <div className={`min-h-screen ${customStyles.background} py-8 px-4`}>
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8">
        <h2 className="text-3xl font-bold mb-8 text-center">{title}</h2>

        {/* Compact shipping + payment + summary */}
        <div className="space-y-8">
          {/* Shipping */}
          <div>
            <h3 className="text-xl font-bold mb-4">{shippingTitle}</h3>
            {/* Compact form */}
          </div>

          {/* Payment */}
          <div>
            <h3 className="text-xl font-bold mb-4">{paymentTitle}</h3>
            {/* Compact payment */}
          </div>

          {/* Summary */}
          <div className="border-t pt-6">
            <h3 className="text-xl font-bold mb-4">Summary</h3>
            <div className="flex justify-between font-bold text-xl">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
          >
            {checkoutText}
          </button>
        </div>
      </div>
    </div>
  );

  const renderCheckoutDesign = () => {
    switch (checkoutDesign) {
      case 'side-by-side': return <Design1 />;
      case 'wizard': return <Design2 />;
      case 'compact': return <Design3 />;
      default: return <Design1 />;
    }
  };

 

  return (
    <div className="flex flex-col min-h-screen relative">
      {/* Flash Bar */}
    <style>{`
       @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Quicksand:wght@400;500;600;700&display=swap');
     html,body{
      font-family: ${config.FONT_FAMILY}, sans-serif !important;
      `}
      </style>
       <ShareableNav/>
      
     
      

     
      <main className="flex-grow" style={containerStyles}>
      <br/><br/><br/><br/><br/>
<br/>
        {renderCheckoutDesign()}
      </main>
       

      {/* Footer */}
      {footerConfig && <FooterSection config={footerConfig} />}
    </div>
  );
};

export default CheckoutPage;